import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from '@/components/utils/translations';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PopoverStaysOpen, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  normalizeDeal,
  getDecisionWindowBandForClosedDeal,
  getDecisionWindowColor,
  getIncumbentSentimentColor,
  canonicalizeSentiment,
} from '@/utils/procurementDealUtils';
import { translateIncumbentSentiment } from '@/lib/accountClassificationLabels';
import {
  getCategoriesForTenant,
  getVendorsForCategories,
  getDealCategoriesForTenant,
  dealStringsToInternalCategories,
} from '@/lib/procurementCompetitors';
import { useProcurementServerIntelligence } from '@/hooks/useProcurementServerIntelligence';
import DealValueDisplay from './DealValueDisplay';
import CompetitorIntelligenceMap from './CompetitorIntelligenceMap';
import { useDealExitLeverage, dealMatchesExitFilter } from '@/hooks/useDealExitLeverage';
import { toDisplayCountryName } from '@/utils/countryCentroids';
import { getDealDisplayValueUSD, getDealValueUSDForTotals } from '@/utils/procurementValueUsd';
import { filterVisibleDeals } from '@/lib/procurementVisibility';
import { dealsToTableRows, sliceTablePage } from '@/lib/contractTablePagination';
import {
  getDealCategories,
  getDealCategoryDisplayFromCategoryField,
  bundledContractNote,
} from '@/utils/procurementCategoryUtils';
import { shouldCollapseLmsFamily } from '@/lib/procurementCompetitors';
import { procurementMethodDisplay } from '@/lib/procurementMethod';

/** Stable empty array for unset filter props — a fresh [] each render refires the data hook. */
const EMPTY_FILTER = [];
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

function getEffectiveSentiment(deal) {
  return canonicalizeSentiment(deal.incumbent_sentiment ?? deal.account?.incumbent_sentiment ?? '');
}

// Decision-window band values are logic values (colours, comparisons); translate only for display.
const DECISION_BAND_LABEL_KEYS = {
  '0–6 months': 'months0_6',
  '6–12 months': 'months6_12',
  '12–24 months': 'months12_24',
  '24+ months': 'procMonths24Plus',
  Expired: 'expired',
  Unknown: 'unknown',
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString();
  } catch { return ''; }
};

function CompetitorIntelligence({
  closedDeals,
  onOpenDetail,
  currentTenant,
  controlledFilters,
  /** When user picks/clears a country on the market map, sync parent filters (e.g. Procurement country chips). */
  onCountryMapSelect,
  /** Path A: load aggregate + paginated table from server instead of client 30–60k filter. */
  useServerAggregates = false,
  /** Pre-filtered closed deals (used when secondary filters force a client path). */
  closedDealsFiltered = null,
  procurementFilters = {},
}) {
  const { t, language } = useTranslation();
  const [compCategories, setCompCategories] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [compInstitutionTypes, setCompInstitutionTypes] = useState([]);
  const [compCountries, setCompCountries] = useState([]);
  const [compSentiments, setCompSentiments] = useState([]);
  const [selectedMapCountry, setSelectedMapCountry] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'soonest', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const skipInitialMapCountrySyncRef = useRef(true);

  useEffect(() => {
    if (!onCountryMapSelect) return;
    if (skipInitialMapCountrySyncRef.current) {
      skipInitialMapCountrySyncRef.current = false;
      return;
    }
    onCountryMapSelect(selectedMapCountry);
  }, [selectedMapCountry, onCountryMapSelect]);

  const useControlled = !!controlledFilters;
  const categories = useControlled ? (controlledFilters.categories || []) : compCategories;
  const countries = useControlled ? (controlledFilters.countries || []) : compCountries;
  const institutionTypes = useControlled ? (controlledFilters.institutionTypes || []) : compInstitutionTypes;
  // [2026-10-17] Status (closed_deals.status). EMPTY_FILTER, not a literal [], because a fresh
  // array every render would re-key the hook's baseFilters memo and refire load() in a loop.
  const statuses = useControlled ? (controlledFilters.statuses || EMPTY_FILTER) : EMPTY_FILTER;
  const sentiments = useControlled ? (controlledFilters.sentiments || []) : compSentiments;
  const vendors = useControlled ? (controlledFilters.vendors || []) : selectedVendors;
  const productNames = useControlled ? (controlledFilters.productNames || []) : [];
  const frameworks = useControlled ? (controlledFilters.frameworks || []) : [];
  const resellers = useControlled ? (controlledFilters.resellers || []) : [];
  const decisionWindows = useControlled ? (controlledFilters.decisionWindows || []) : [];
  const exitFilter = useControlled ? (controlledFilters.exitFilter || 'all') : 'all';

  const categoryOptions = useMemo(() => {
    const excluded = ['Defense Technology', 'Adaptive Learning / Authoring + Platform', 'ICT Investment'];
    const set = new Set();
    closedDeals.forEach((d) => {
      getDealCategories(d).forEach((cat) => {
        if (cat && !excluded.includes(cat)) set.add(cat);
      });
    });
    return Array.from(set).sort();
  }, [closedDeals]);

  const defaultCategories = useMemo(() => [], []);
  useEffect(() => {
    if (!useControlled) setCompCategories(defaultCategories);
  }, [defaultCategories.join(','), useControlled]);

  const effectiveDealCategories = (useControlled ? categories : compCategories).length === 0 ? [] : (useControlled ? categories : compCategories);
  const effectiveInternalCategories = dealStringsToInternalCategories(effectiveDealCategories);

  const availableVendors = useMemo(
    () => getVendorsForCategories(effectiveInternalCategories.length ? effectiveInternalCategories : getCategoriesForTenant(currentTenant), currentTenant, true),
    [effectiveInternalCategories, currentTenant]
  );

  const availableSet = new Set(availableVendors.map((v) => v.toLowerCase()));
  const effectiveVendors =
    vendors.length === 0
      ? []
      : (useControlled ? vendors : vendors.filter((v) => availableSet.has(v.toLowerCase())));

  const serverIntel = useProcurementServerIntelligence({
    enabled: Boolean(useServerAggregates && currentTenant),
    tenantId: currentTenant,
    categories,
    countries,
    institutionTypes,
    statuses,
    suppliers: effectiveVendors.length ? effectiveVendors : vendors,
    productNames,
    frameworks,
    resellers,
    sentiments,
    decisionWindows,
    search: procurementFilters.search ?? '',
    // [2026-10-17] Sent again. p_exit_filter used to return zero rows for EVERY value (measured
    // strong=0, some=0, none=0 against all=1,546/4,041) because deal_exit_leverage.exit_tag
    // stores 'Strong'/'Some' while the route whitelists lowercase, and 'none' has no stored form
    // at all. 2026-10-17_aggregate_framework_reseller_exit fixes both in SQL and ports
    // shouldShowExitLeverage's source_family x country gate into procurement_exit_visible, so the
    // server is once again the single source of truth for the table AND the map.
    exitFilter,
    sortConfig,
    selectedMapCountry,
    pageSize: itemsPerPage,
  });

  const clientDeals = useMemo(() => {
    if (useServerAggregates) {
      const source = filterVisibleDeals(serverIntel.rows);
      if (!selectedMapCountry) return source;
      return source.filter((d) => {
        const label = toDisplayCountryName(d.country) || String(d.country || '').trim();
        return label && label.toLowerCase() === String(selectedMapCountry).toLowerCase();
      });
    }
    const corpus = filterVisibleDeals(closedDealsFiltered != null ? closedDealsFiltered : closedDeals);
    if (!selectedMapCountry) return corpus;
    return corpus.filter((d) => {
      const label = toDisplayCountryName(d.country) || String(d.country || '').trim();
      return label && label.toLowerCase() === String(selectedMapCountry).toLowerCase();
    });
  }, [useServerAggregates, serverIntel.rows, closedDealsFiltered, closedDeals, selectedMapCountry]);

  const { map: exitLeverageMap } = useDealExitLeverage(
    currentTenant,
    clientDeals,
  );

  // Server path: the RPC has already applied it (and the map agrees, which a client-side pass
  // could never achieve - the client holds one row per account, not all 4,041 deals).
  const clientDealsAfterExit = useMemo(() => {
    if (useServerAggregates) return clientDeals;
    if (!exitFilter || exitFilter === 'all') return clientDeals;
    return clientDeals.filter((d) => dealMatchesExitFilter(d, exitLeverageMap, exitFilter));
  }, [useServerAggregates, clientDeals, exitLeverageMap, exitFilter]);

  const clientTableList = useMemo(() => {
    // [2026-10-17] Predicted Expiry orders from TODAY ONWARDS. Sorting the raw date ascending put
    // 2017 expiries at the top of the default view — the oldest and least actionable rows first.
    // Rows are bucketed instead: still-upcoming (nearest first), then already-past (most recent
    // first), then rows with no predicted expiry. Nothing is dropped and no row is hidden; a header
    // click flips the order WITHIN each bucket, so past/unknown stay parked at the bottom either way.
    const todayMs = new Date(new Date().toDateString()).getTime();
    const expiryMs = (d) => {
      const raw = d.predicted_expiry ?? d.predicted_expiry_date;
      if (!raw) return null;
      const t = new Date(raw).getTime();
      return Number.isNaN(t) ? null : t;
    };
    const expiryBucket = (t) => (t == null ? 2 : t >= todayMs ? 0 : 1);
    const sorted = [...clientDealsAfterExit].sort((a, b) => {
      const key = sortConfig.key;
      const dir = sortConfig.direction === 'ascending' ? 1 : -1;
      if (key === 'soonest' || key === 'decision_window' || key === 'expiry') {
        const at = expiryMs(a);
        const bt = expiryMs(b);
        const ab = expiryBucket(at);
        const bb = expiryBucket(bt);
        if (ab !== bb) return ab - bb;
        if (ab === 2) return 0;
        // Upcoming: nearest first. Past: most recent first. `dir` flips within the bucket only.
        return (ab === 0 ? at - bt : bt - at) * dir;
      }
      if (key === 'value') {
        return (getDealDisplayValueUSD(a) - getDealDisplayValueUSD(b)) * dir;
      }
      if (key === 'procurement_method') {
        // [2026-10-17] Sorts on the rendered label, not a column: the method and its framework
        // line are both derived by procurementMethodDisplay, so `a.procurement_method` (what the
        // generic branch below would have read) does not exist. Method first, then the framework
        // name, so the two-line cell groups the way it reads. Rows with neither sort last.
        const lab = (x) => {
          const m = procurementMethodDisplay(x);
          return [m.method || '', m.framework || ''].join(' \u00b7 ').trim();
        };
        const av = lab(a).toLowerCase();
        const bv = lab(b).toLowerCase();
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return av.localeCompare(bv) * dir;
      }
      if (key === 'sentiment') {
        // Rank explicitly, and keep unknowns LAST in both directions. Relying on
        // localeCompare of the canonical strings only worked by alphabetical
        // accident (negative < neutral < positive < unknown), and it disagreed
        // with the server, which used to rank blanks between neutral and
        // positive. Two different orders over the same rows put a blank row in
        // the middle of the neutrals at a page boundary. Server now ranks
        // blanks last too; this keeps the client honest regardless.
        const RANK = { negative: 0, neutral: 1, positive: 2 };
        const ar = RANK[getEffectiveSentiment(a)];
        const br = RANK[getEffectiveSentiment(b)];
        if (ar === undefined && br === undefined) return 0;
        if (ar === undefined) return 1;
        if (br === undefined) return -1;
        return (ar - br) * dir;
      }
      // Blank cells sort last rather than falling back to the account name,
      // which made the comparator inconsistent and scattered them.
      const av = String(a[key] ?? '').trim().toLowerCase();
      const bv = String(b[key] ?? '').trim().toLowerCase();
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return av.localeCompare(bv) * dir;
    });

    return dealsToTableRows(sorted, { selectedMapCountry });
  }, [clientDealsAfterExit, sortConfig, selectedMapCountry]);

  const clientCountryDealCounts = useMemo(() => {
    if (useServerAggregates) return null;
    const o = {};
    clientDealsAfterExit.forEach((d) => {
      const c = toDisplayCountryName(d.country) || String(d.country || '').trim();
      if (!c) return;
      o[c] = (o[c] || 0) + 1;
    });
    return o;
  }, [useServerAggregates, clientDealsAfterExit]);

  const clientSummary = useMemo(() => {
    if (useServerAggregates) return null;
    const countrySet = new Set();
    let totalValueUSD = 0;
    clientDealsAfterExit.forEach((d) => {
      const c = toDisplayCountryName(d.country) || String(d.country || '').trim();
      if (c) countrySet.add(c);
      totalValueUSD += getDealValueUSDForTotals(d);
    });
    return {
      totalCountries: countrySet.size,
      totalDeals: clientDealsAfterExit.length,
      totalValueUSD,
      top3Vendors: [],
      topVendorsByDealCount: true,
    };
  }, [useServerAggregates, clientDealsAfterExit]);

  const clientTotalPages = Math.max(1, Math.ceil(clientTableList.length / itemsPerPage));
  const clientPageSlice = useMemo(
    () => sliceTablePage(clientTableList, currentPage, itemsPerPage),
    [clientTableList, currentPage, itemsPerPage],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    useServerAggregates,
    selectedMapCountry,
    sortConfig.key,
    sortConfig.direction,
    closedDealsFiltered,
    sentiments,
    productNames,
    decisionWindows,
    exitFilter,
    serverIntel.rows,
  ]);

  useEffect(() => {
    if (currentPage > clientTotalPages) setCurrentPage(1);
  }, [currentPage, clientTotalPages]);

  const paginatedAttackList = clientPageSlice;
  const displayTableDealCount = clientDealsAfterExit.length;
  const displayAccountCount = clientTableList.length;
  const totalPages = clientTotalPages;
  const contractSummaryResolved = useServerAggregates ? serverIntel.summary : clientSummary;
  const byCountryDealCountsResolved = useServerAggregates
    ? serverIntel.byCountryDealCounts
    : clientCountryDealCounts;
  const mapRowsForCountry = clientDealsAfterExit;
  const mapCountryValueUSD = useServerAggregates ? serverIntel.byCountryValueUSD : undefined;
  const mapGlobalVendorPieData = useServerAggregates
    ? serverIntel.mergedVendors.map((v) => ({
        name: v.vendor,
        value: v.deal_count,
      }))
    : undefined;
  const marketShareDeals = useMemo(() => {
    const source = filterVisibleDeals(useServerAggregates ? serverIntel.rows : clientDealsAfterExit);
    if (!selectedMapCountry) return source;
    return source.filter((d) => {
      const label = toDisplayCountryName(d.country) || String(d.country || '').trim();
      return label && label.toLowerCase() === String(selectedMapCountry).toLowerCase();
    });
  }, [useServerAggregates, clientDealsAfterExit, selectedMapCountry, serverIntel.rows]);
  const shareCountryCounts = byCountryDealCountsResolved;
  const shareCountryValueUSD = useServerAggregates ? mapCountryValueUSD : undefined;
  const hasMatchingDeals = displayTableDealCount > 0;
  const activePage = currentPage;
  const goToPage = (pageNum) => setCurrentPage(pageNum);

  const SortableHeader = ({ sortKey, children, className }) => {
    const isSorted = sortConfig.key === sortKey;
    const handleSort = () => {
      setSortConfig((prev) => ({
        key: sortKey,
        direction: prev.key === sortKey && prev.direction === 'ascending' ? 'descending' : 'ascending',
      }));
    };
    return (
      <TableHead onClick={handleSort} className={`cursor-pointer hover:bg-gray-50 select-none align-top whitespace-normal max-w-0 ${className || ''}`}>
        <div className="flex items-start gap-1 min-w-0 whitespace-normal break-words">
          <span className="break-words">{children}</span>
          {isSorted && (sortConfig.direction === 'ascending' ? <ArrowUp className="w-3 h-3 shrink-0 mt-0.5" /> : <ArrowDown className="w-3 h-3 shrink-0 mt-0.5" />)}
        </div>
      </TableHead>
    );
  };


  const uniqueInstitutionTypes = useMemo(() => {
    const set = new Set();
    closedDeals.forEach((d) => {
      const it = d.institution_type || d.account?.institution_type;
      if (it) set.add(it);
    });
    return Array.from(set).filter(Boolean).sort();
  }, [closedDeals]);

  const uniqueCountries = useMemo(() => {
    const set = new Set();
    closedDeals.forEach((d) => {
      const label = toDisplayCountryName(d.country);
      if (label) {
        set.add(label);
      } else {
        const c = (d.country || '').trim();
        if (c) set.add(c);
      }
    });
    return Array.from(set).sort();
  }, [closedDeals]);

  return (
    <div className="space-y-6">
      {!useControlled && (
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b py-3 -mx-2 px-2 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <PopoverStaysOpen>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-sm">
                {t('category')}
                {compCategories.length > 0 ? ` (${compCategories.length})` : ''}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 max-h-[min(320px,70vh)] overflow-y-auto" align="start">
              <div className="flex gap-2 pb-2 border-b text-xs mb-2">
                <button
                  type="button"
                  className="text-[#00C2C2] hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompCategories([...categoryOptions]); }}
                >
                  {t('selectAll')}
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompCategories(defaultCategories); }}
                >
                  {t('default')}
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompCategories([]); }}
                >
                  {t('clearAll')}
                </button>
              </div>
              {categoryOptions.map((c) => (
                <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                  <Checkbox
                    checked={compCategories.includes(c)}
                    onCheckedChange={(v) =>
                      setCompCategories((p) =>
                        v ? [...p, c] : p.filter((x) => x !== c)
                      )
                    }
                  />
                  <span className="text-sm">{c}</span>
                </label>
              ))}
            </PopoverContent>
          </PopoverStaysOpen>
          <PopoverStaysOpen>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-sm">
                {t('vendor')}
                {selectedVendors.length > 0 ? ` (${selectedVendors.length})` : ''}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 max-h-[min(320px,70vh)] overflow-y-auto" align="start">
              <div className="flex gap-2 pb-2 border-b text-xs mb-2">
                <button
                  type="button"
                  className="text-[#00C2C2] hover:underline"
                  onClick={(e) => { e.stopPropagation(); setSelectedVendors([]); }}
                >
                  {t('all')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-2">{t('procVendorsForSelectedCategories')}</p>
              {availableVendors.map((c) => (
                <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                  <Checkbox
                    checked={selectedVendors.length === 0 || selectedVendors.includes(c)}
                    onCheckedChange={(v) => {
                      if (v) {
                        const base = selectedVendors.length === 0 ? availableVendors : selectedVendors;
                        const next = [...base.filter((x) => x !== c), c];
                        setSelectedVendors(next.length >= availableVendors.length ? [] : next);
                      } else {
                        setSelectedVendors(selectedVendors.length === 0
                          ? availableVendors.filter((x) => x !== c)
                          : selectedVendors.filter((x) => x !== c));
                      }
                    }}
                  />
                  <span className="text-sm">{c}</span>
                </label>
              ))}
            </PopoverContent>
          </PopoverStaysOpen>
          <PopoverStaysOpen>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-sm">
                {t('institutionType')}
                {compInstitutionTypes.length > 0 ? ` (${compInstitutionTypes.length})` : ''}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 max-h-[min(320px,70vh)] overflow-y-auto" align="start">
              <div className="flex gap-2 pb-2 border-b text-xs mb-2">
                <button
                  type="button"
                  className="text-[#00C2C2] hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompInstitutionTypes([...uniqueInstitutionTypes]); }}
                >
                  {t('selectAll')}
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompInstitutionTypes([]); }}
                >
                  {t('clearAll')}
                </button>
              </div>
              {uniqueInstitutionTypes.map((c) => (
                <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                  <Checkbox
                    checked={compInstitutionTypes.includes(c)}
                    onCheckedChange={(v) =>
                      setCompInstitutionTypes((p) => (v ? [...p, c] : p.filter((x) => x !== c)))
                    }
                  />
                  <span className="text-sm">{c}</span>
                </label>
              ))}
            </PopoverContent>
          </PopoverStaysOpen>
          <PopoverStaysOpen>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-sm">
                {t('country')}
                {compCountries.length > 0 ? ` (${compCountries.length})` : ''}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 max-h-[min(320px,70vh)] overflow-y-auto" align="start">
              <div className="flex gap-2 pb-2 border-b text-xs mb-2">
                <button
                  type="button"
                  className="text-[#00C2C2] hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompCountries([...uniqueCountries]); }}
                >
                  {t('selectAll')}
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompCountries([]); }}
                >
                  {t('clearAll')}
                </button>
              </div>
              {uniqueCountries.map((c) => (
                <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                  <Checkbox
                    checked={compCountries.includes(c)}
                    onCheckedChange={(v) =>
                      setCompCountries((p) => (v ? [...p, c] : p.filter((x) => x !== c)))
                    }
                  />
                  <span className="text-sm">{c}</span>
                </label>
              ))}
            </PopoverContent>
          </PopoverStaysOpen>
          <PopoverStaysOpen>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-sm">
                {t('sentiment')}
                {compSentiments.length > 0 ? ` (${compSentiments.length})` : ''}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 max-h-[min(320px,70vh)] overflow-y-auto" align="start">
              <div className="flex gap-2 pb-2 border-b text-xs mb-2">
                <button
                  type="button"
                  className="text-[#00C2C2] hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompSentiments(['negative', 'neutral', 'positive', 'unknown']); }}
                >
                  {t('selectAll')}
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCompSentiments([]); }}
                >
                  {t('clearAll')}
                </button>
              </div>
              {['negative', 'neutral', 'positive', 'unknown'].map((s) => (
                <label key={s} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                  <Checkbox
                    checked={compSentiments.length === 0 || compSentiments.includes(s)}
                    onCheckedChange={(v) => {
                      if (v) {
                        const base = compSentiments.length === 0 ? ['negative', 'neutral', 'positive', 'unknown'] : compSentiments;
                        const next = [...base.filter((x) => x !== s), s];
                        setCompSentiments(next.length >= 4 ? [] : next);
                      } else {
                        setCompSentiments(compSentiments.length === 0
                          ? ['negative', 'neutral', 'positive', 'unknown'].filter((x) => x !== s)
                          : compSentiments.filter((x) => x !== s));
                      }
                    }}
                  />
                  <span className="text-sm capitalize">{translateIncumbentSentiment(language, s)}</span>
                </label>
              ))}
            </PopoverContent>
          </PopoverStaysOpen>
          {([...compCategories].sort().join() !== [...defaultCategories].sort().join() ||
            compInstitutionTypes.length > 0 ||
            compCountries.length > 0 ||
            compSentiments.length > 0 ||
            selectedVendors.length > 0) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-sm"
              onClick={() => {
                setCompCategories(defaultCategories);
                setSelectedVendors([]);
                setCompInstitutionTypes([]);
                setCompCountries([]);
                setCompSentiments([]);
                setSelectedMapCountry(null);
              }}
            >
              {t('clearFilters')}
            </Button>
          )}
        </div>
      </div>
      )}

      <TooltipProvider delayDuration={100}>
      {/*
        [2026-10-17] Re-filter progress. The spinner below only covers the FIRST load
        (`!aggregate && count === 0`). Every later filter change refetches every matching row —
        ~1,500 for the unfiltered set, which takes several seconds — and until this bar existed the
        previous, narrower result stayed on screen with no sign anything was happening. Measured:
        unchecking an institution type left the count at 59 for ~10s before it returned to 1,546,
        which is exactly why it read as "unticking it doesn't revert".
      */}
      {useServerAggregates && serverIntel.loading && (serverIntel.aggregate || displayTableDealCount > 0) && (
        <div className="h-1 w-full rounded bg-[#00C2C2]/30 overflow-hidden mb-2" role="status" aria-label={t('procUpdatingResults')}>
          <div className="h-full bg-[#00C2C2] animate-pulse w-2/3 rounded" />
        </div>
      )}
      {useServerAggregates && serverIntel.loading && !serverIntel.aggregate && displayTableDealCount === 0 ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#00C2C2]" />
        </div>
      ) : useServerAggregates && serverIntel.error && displayTableDealCount === 0 ? (
        <div className="py-12 text-center text-red-600 text-sm">{serverIntel.error}</div>
      ) : !hasMatchingDeals ? (
        <div className="py-12 text-center text-gray-500">
          {t('noRecordsSelectedFilters')}
        </div>
      ) : (
        <>
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('marketMap')}</h3>
            <p className="text-xs text-gray-500 mb-3">
              {t('marketMapHelpText')}
            </p>
            <CompetitorIntelligenceMap
              deals={selectedMapCountry ? mapRowsForCountry : []}
              dealsForPie={marketShareDeals}
              selectedCountry={selectedMapCountry}
              onCountrySelect={(c) => {
                const label = toDisplayCountryName(c) || c;
                setSelectedMapCountry((prev) => (prev === label ? null : label));
              }}
              onClearSelection={() => setSelectedMapCountry(null)}
              currentTenant={currentTenant}
              summary={contractSummaryResolved}
              showTopVendors={effectiveVendors.length > 1}
              onOpenDetail={onOpenDetail}
              countryDealCounts={shareCountryCounts}
              countryValueUSD={shareCountryValueUSD}
              globalVendorPieData={mapGlobalVendorPieData}
              collapseLmsFamily={shouldCollapseLmsFamily(categories)}
            />
          </section>

          {hasMatchingDeals && (
          <section>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-xs text-gray-500">{t('procClickHeadersToSort')}</p>
              {(frameworks.length > 0 || resellers.length > 0) && (
                <p className="text-xs text-gray-500">
                  {/* Table-only by decision (2026-10-13): the map and totals above are not narrowed. */}
                  {[frameworks.length ? t('framework') : null, resellers.length ? t('reseller') : null].filter(Boolean).join(' / ')} {t('procFilterAppliesTableOnly')}
                </p>
              )}
            </div>
            <div className="border rounded-lg overflow-x-auto -mx-2 px-2">
              <Table className="table-fixed w-full min-w-[1080px]">
                <colgroup>
                  {/* institution · country · category · reseller · vendor · product · method · window · expiry · sentiment · value = 100% */}
                  <col className="w-[12%]" />
                  <col className="w-[7%]" />
                  <col className="w-[10%]" />
                  <col className="w-[9%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[11%]" />
                  <col className="w-[8%]" />
                  <col className="w-[7%]" />
                  <col className="w-[8%]" />
                  <col className="w-[8%]" />
                </colgroup>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <SortableHeader sortKey="institution" className="pr-2">{t('institution')}</SortableHeader>
                    <SortableHeader sortKey="country" className="pr-2">{t('country')}</SortableHeader>
                    <SortableHeader sortKey="category" className="pr-2">{t('category')}</SortableHeader>
                    <SortableHeader sortKey="reseller" className="pr-2">{t('reseller')}</SortableHeader>
                    <SortableHeader sortKey="supplier" className="pr-2">{t('vendor')}</SortableHeader>
                    <SortableHeader sortKey="product_name" className="pr-2">{t('product')}</SortableHeader>
                    {/*
                      [2026-10-17] Now sortable. The old note said the server's sort whitelist has
                      no method key - true, but this table is NOT server-paginated: every matching
                      row is fetched and clientTableList sorts and pages them here, so the key never
                      had to exist server-side. mapSortKeyToApi falls through to its default for an
                      unknown key, so the request stays valid and the client comparator does the work.
                    */}
                    <SortableHeader sortKey="procurement_method" className="pr-2 text-xs">{t('procurementMethod')}</SortableHeader>
                    <SortableHeader sortKey="decision_window" className="pr-2">{t('decisionWindow')}</SortableHeader>
                    <SortableHeader sortKey="expiry" className="pr-2 text-xs">{t('predictedExpiry')}</SortableHeader>
                    <SortableHeader sortKey="sentiment" className="pr-2">{t('sentiment')}</SortableHeader>
                    <SortableHeader sortKey="value" className="pr-2">{t('value')}</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAttackList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                        {selectedMapCountry
                          ? t('noDealsCountryFilters')
                          : t('noAttackableAccounts')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAttackList.map((deal) => {
                      const d = normalizeDeal(deal, deal.account);
                      const { band } = getDecisionWindowBandForClosedDeal(deal);
                      const bandColor = getDecisionWindowColor(band);
                      const sentimentVal = d.incumbent_sentiment ?? deal.incumbent_sentiment ?? deal.incumbentSentiment ?? deal.account?.incumbent_sentiment ?? deal.account?.incumbentSentiment ?? null;
                      const rationaleVal = d.incumbent_sentiment_rationale ?? deal.incumbent_sentiment_rationale ?? deal.incumbentSentimentRationale ?? deal.account?.incumbent_sentiment_rationale ?? deal.account?.incumbentSentimentRationale ?? null;
                      const sentimentColor = getIncumbentSentimentColor(sentimentVal);
                      return (
                        <TableRow key={deal.deal_id ?? deal.id ?? `${deal.account_name}|${deal.supplier}|${deal.country}`}>
                          <TableCell
                            className="font-medium cursor-pointer hover:text-[#00C2C2] pr-2 align-top whitespace-normal break-words max-w-0"
                            onClick={() => onOpenDetail?.(deal)}
                          >
                            {d.institution || ''}
                          </TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">{d.country || ''}</TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">
                            {getDealCategoryDisplayFromCategoryField(d) || d.category || ''}
                            {/* [2026-10-27] procurement_contract_table flags a row reached through
                                matched_categories (an LMS contract covering Online Exams). Say so, and
                                that the value is the whole contract — it is never split per category. */}
                            {deal?.bundled === true && (
                              <div className="text-[10px] text-gray-500 mt-0.5" title={bundledContractNote(deal) || undefined}>
                                {t('procBundledContract')}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">{d.reseller ?? deal.reseller ?? ''}</TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">{d.supplier ?? deal.supplier ?? deal.vendor ?? deal.incumbent_vendor ?? ''}</TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">{d.product_name ?? deal.product_name ?? ''}</TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">
                            {(() => {
                              const m = procurementMethodDisplay(deal);
                              return (
                                <>
                                  {m.method ? <p>{m.method}</p> : null}
                                  {m.framework ? (
                                    <p className="text-gray-500 mt-0.5" title={m.frameworkTitle || undefined}>{m.framework}</p>
                                  ) : null}
                                </>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="pr-2 align-top whitespace-normal max-w-0">
                            <Badge variant="outline" className={`text-xs border max-w-full whitespace-normal break-words ${bandColor}`}>
                              {DECISION_BAND_LABEL_KEYS[band] ? t(DECISION_BAND_LABEL_KEYS[band]) : band}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 pr-2 align-top whitespace-normal break-words max-w-0">{formatDate(deal.predicted_expiry_date || deal.predicted_expiry)}</TableCell>
                          <TableCell className="pr-2 align-top whitespace-normal max-w-0">
                            {band === 'Expired' || canonicalizeSentiment(sentimentVal) === 'unknown' ? null : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-block max-w-full cursor-help">
                                    <Badge variant="outline" className={`text-xs border max-w-full whitespace-normal break-words ${sentimentColor}`}>
                                      {translateIncumbentSentiment(language, sentimentVal)}
                                    </Badge>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px] text-xs whitespace-pre-line">
                                  {rationaleVal || t('procNoRationaleProvided')}
                                </TooltipContent>
                              </Tooltip>
                              )}
                          </TableCell>
                          <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">
                            <DealValueDisplay deal={deal} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => goToPage(Math.max(1, activePage - 1))}
                        disabled={activePage === 1}
                        className="gap-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {t('previous')}
                      </Button>
                    </PaginationItem>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (activePage <= 3) {
                        pageNum = i + 1;
                      } else if (activePage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = activePage - 2 + i;
                      }
                      return (
                        <PaginationItem key={pageNum}>
                          <Button
                            variant={activePage === pageNum ? 'outline' : 'ghost'}
                            size="sm"
                            onClick={() => goToPage(pageNum)}
                            className="min-w-[2.5rem]"
                          >
                            {pageNum}
                          </Button>
                        </PaginationItem>
                      );
                    })}
                    <PaginationItem>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => goToPage(Math.min(totalPages, activePage + 1))}
                        disabled={activePage === totalPages}
                        className="gap-1"
                      >
                        {t('next')}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
            {displayAccountCount > 0 && (
              <div className="mt-2 text-xs text-gray-500 text-center">
                {t(selectedMapCountry ? 'procShowingRangeDeals' : 'procShowingRangeAccounts', {
                  from: ((activePage - 1) * itemsPerPage) + 1,
                  to: Math.min(activePage * itemsPerPage, displayAccountCount),
                  total: displayAccountCount.toLocaleString(),
                })}
                {!selectedMapCountry && (
                  ` (${t('procMatchingDealsOnePerAccount', { n: displayTableDealCount.toLocaleString() })})`
                )}
              </div>
            )}
          </section>
          )}
        </>
      )}
      </TooltipProvider>
    </div>
  );
}

export default React.memo(CompetitorIntelligence);
